// JavaScript API for AI interaction with zones and pins

import type { Zone, ContextPin, ZoneState, ZoneType, PinType, ZoneTypeDefinition, PinTypeDefinition } from '@/types/zone'
import { zoneState } from './ZoneState'
import { ALL_ZONE_TYPES, PIN_TYPES } from '@/constants/zoneTypes'
import { debug } from '@/utils/debug'

/**
 * API interface for AI and external scripts
 */
export interface ZoneAPIInterface {
  // Zone operations
  addZone(type: ZoneType, x: number, y: number, width: number, height: number, options?: {
    rotation?: number
    elevation?: number
    label?: string
    notes?: string
    points?: Array<{ x: number; y: number }>
  }): string
  updateZone(id: string, updates: Partial<Omit<Zone, 'id' | 'type'>>): void
  deleteZone(id: string): boolean
  getZones(): Zone[]
  getZoneById(id: string): Zone | undefined

  // Pin operations
  addPin(type: PinType, x: number, y: number, options?: {
    label?: string
    photoUrl?: string
    notes?: string
  }): string
  updatePin(id: string, updates: Partial<Omit<ContextPin, 'id' | 'type'>>): void
  deletePin(id: string): boolean
  getPins(): ContextPin[]
  getPinById(id: string): ContextPin | undefined

  // Bulk operations
  clearAll(): void
  importState(state: ZoneState): void
  exportState(): ZoneState

  // Utility
  getZoneTypes(): ZoneTypeDefinition[]
  getPinTypes(): PinTypeDefinition[]
}

/**
 * Zone API implementation
 */
class ZoneAPIImpl implements ZoneAPIInterface {
  // ============================================================================
  // Zone operations
  // ============================================================================

  addZone(
    type: ZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    options?: {
      rotation?: number
      elevation?: number
      label?: string
      notes?: string
      points?: Array<{ x: number; y: number }>
    }
  ): string {
    return zoneState.addZone(type, x, y, width, height, options)
  }

  updateZone(id: string, updates: Partial<Omit<Zone, 'id' | 'type'>>): void {
    zoneState.updateZone(id, updates)
  }

  deleteZone(id: string): boolean {
    return zoneState.deleteZone(id)
  }

  getZones(): Zone[] {
    return zoneState.getZones()
  }

  getZoneById(id: string): Zone | undefined {
    return zoneState.getZoneById(id)
  }

  // ============================================================================
  // Pin operations
  // ============================================================================

  addPin(
    type: PinType,
    x: number,
    y: number,
    options?: {
      label?: string
      photoUrl?: string
      notes?: string
    }
  ): string {
    return zoneState.addPin(type, x, y, options)
  }

  updatePin(id: string, updates: Partial<Omit<ContextPin, 'id' | 'type'>>): void {
    zoneState.updatePin(id, updates)
  }

  deletePin(id: string): boolean {
    return zoneState.deletePin(id)
  }

  getPins(): ContextPin[] {
    return zoneState.getPins()
  }

  getPinById(id: string): ContextPin | undefined {
    return zoneState.getPinById(id)
  }

  // ============================================================================
  // Bulk operations
  // ============================================================================

  clearAll(): void {
    zoneState.clearAll()
  }

  importState(state: ZoneState): void {
    zoneState.importState(state)
  }

  exportState(): ZoneState {
    return zoneState.exportState()
  }

  // ============================================================================
  // Utility
  // ============================================================================

  getZoneTypes(): ZoneTypeDefinition[] {
    return [...ALL_ZONE_TYPES]
  }

  getPinTypes(): PinTypeDefinition[] {
    return [...PIN_TYPES]
  }
}

// Singleton instance
export const zoneAPI = new ZoneAPIImpl()

// Type alias for external use
export type { ZoneAPIInterface as ZoneAPI }

/**
 * Expose API globally for AI and console access
 */
export function exposeZoneAPI(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).gardenZones = zoneAPI

  debug.log('ZoneAPI', 'API exposed at window.gardenZones')

  // Log available methods for AI discovery
  console.log('%c[Garden Zones API]%c Available at window.gardenZones', 'color: #4ecca3; font-weight: bold', 'color: inherit')
  console.log('Methods:', {
    zones: ['addZone', 'updateZone', 'deleteZone', 'getZones', 'getZoneById'],
    pins: ['addPin', 'updatePin', 'deletePin', 'getPins', 'getPinById'],
    bulk: ['clearAll', 'importState', 'exportState'],
    info: ['getZoneTypes', 'getPinTypes'],
  })
  console.log('Example: gardenZones.addZone("relaxation", 100, 100, 200, 150)')
}
